/**
 * ConversationControls — lifecycle controls (pause/resume/stop) for a
 * conversation, rendered for BOTH ephemeral and normal conversations.
 *
 * Visibility is derived from the live status/control state:
 *   - Pause  : status ∈ {idle, pending, running} and control !== 'pause'
 *   - Resume : status === 'paused' OR control === 'pause' (pause requested but
 *              not yet applied by the worker)
 *   - Stop   : status ∈ {running, pending}
 *
 * Each mutation already invalidates the `AgentConversation` detail tag, so the
 * status badge refreshes on its own; `onChanged` lets the page trigger its own
 * refetch (stream/persistence reconciliation) as well.
 */
import { Pause, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/shared/Toast';
import { apiErrorMessage } from '@/lib/apiError';
import {
  usePauseConversationMutation,
  useResumeConversationMutation,
  useStopConversationMutation,
} from '@/store/api/agentRuntimeApi';
import type { ConversationControl, ConversationStatus } from '@/types';

export interface ConversationControlsProps {
  conversationId: string;
  status: ConversationStatus;
  control?: ConversationControl;
  /** Stop flavour — mirrors the controls-bar select. Defaults to soft. */
  stopMode?: 'soft' | 'hard';
  /** Called after a control is accepted (tag invalidation already refetches). */
  onChanged?: () => void;
}

export function ConversationControls({
  conversationId,
  status,
  control,
  stopMode = 'soft',
  onChanged,
}: ConversationControlsProps) {
  const { success, error: toastError } = useToast();
  const [pause, { isLoading: pausing }] = usePauseConversationMutation();
  const [resume, { isLoading: resuming }] = useResumeConversationMutation();
  const [stop, { isLoading: stopping }] = useStopConversationMutation();

  const showPause =
    (status === 'idle' || status === 'pending' || status === 'running') &&
    control !== 'pause';
  const showResume = status === 'paused' || control === 'pause';
  const showStop = status === 'running' || status === 'pending';

  const runControl = async (
    action: () => Promise<{ control?: ConversationControl }>,
    label: string,
    fallbackError: string,
  ) => {
    try {
      const result = await action();
      success(`${label} — control: ${result?.control ?? 'none'}`);
      onChanged?.();
    } catch (err) {
      toastError(apiErrorMessage(err, fallbackError));
    }
  };

  const handlePause = () =>
    runControl(() => pause(conversationId).unwrap(), 'Pause requested', 'Failed to pause conversation');

  const handleResume = () =>
    runControl(() => resume(conversationId).unwrap(), 'Resume requested', 'Failed to resume conversation');

  const handleStop = () =>
    runControl(
      () => stop({ id: conversationId, hard: stopMode === 'hard' }).unwrap(),
      stopMode === 'hard' ? 'Hard stop requested' : 'Stop requested',
      'Failed to stop conversation',
    );

  if (!showPause && !showResume && !showStop) return null;

  return (
    <div className="flex items-center gap-2">
      {showPause && (
        <Button variant="outline" size="sm" disabled={pausing} onClick={handlePause}>
          <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause
        </Button>
      )}
      {showResume && (
        <Button variant="outline" size="sm" disabled={resuming} onClick={handleResume}>
          <Play className="mr-1.5 h-3.5 w-3.5" /> Resume
        </Button>
      )}
      {showStop && (
        <Button variant="destructive" size="sm" disabled={stopping} onClick={handleStop}>
          <Square className="mr-1.5 h-3.5 w-3.5" /> Stop
        </Button>
      )}
    </div>
  );
}